import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const API_V1_URL = `${API_BASE_URL}/v1`;

interface ParamsCTXRList {
    cloud?: string;
    region?: string;
}

export function getCTXRlistGlobal(
    subscriptionId: number,
    tenant: number,
    token: string,
    cloud?: string,
    region?: string
) {
    const params: ParamsCTXRList = {};
    if (cloud !== undefined) params.cloud = cloud;
    if (region !== undefined) params.region = region;

    return axios.get(
        `${API_V1_URL}/ctxr/${subscriptionId}/listSelector?tenantId=${tenant}`,
        {
            params,
            headers: { Authorization: `Bearer ${token}` },
        }
    );
}

// Ejemplo de función para pedir la lista de "available"
export function getAvailableSyslogs(
    subscriptionId: number,
    ctxrId: string,
    tenant: number,
    token: string
) {
    // https://stg-api.transitxchange.cloud/api/v1/serverlog/{subscriptionId}/available/{ctxrId}?tenantId={tenant}
    const url = `${API_V1_URL}/serverlog/${subscriptionId}/available/${ctxrId}?tenantId=${tenant}`;
    return axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

// Ejemplo de función para pedir la lista de "selected"
export function getSelectedSyslogs(
    subscriptionId: number,
    ctxrId: string,
    tenant: number,
    token: string
) {
    // https://stg-api.transitxchange.cloud/api/v1/serverlog/{subscriptionId}/ctxr/${ctxrId}?tenantId={tenant}
    const url = `${API_V1_URL}/serverlog/${subscriptionId}/ctxr/${ctxrId}?tenantId=${tenant}`;
    return axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

// POST para guardar cambios con el nuevo endpoint
export function saveSyslogs(
    subscriptionId: number,
    ctxrId: string,
    tenant: number,
    token: string,
    syslogs: SyslogItem[]
) {
    // Nuevo endpoint: POST /ctxr/associationwithsyslog/{ctxrId}?tenantId=...
    const url = `${API_V1_URL}/ctxr/associationwithsyslog/${ctxrId}?tenantId=${tenant}`;

    // Armamos el payload como [ { "id": x, "level": y }, ... ]
    const payload = syslogs.map(({ id, level }) => ({
        id,
        level: level ?? 0,
    }));

    return axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

// Tipifica tu SyslogItem según el response real
export interface SyslogItem {
    id: number;
    ip: string;
    port: number;
    description: string;
    level?: number;
}

// ========== V1 Reporting/Syslog Server CRUD Functions ==========

/**
 * Interface for V1 Syslog Server request payload
 */
export interface SyslogServerRequestV1 {
    ip: string;
    port: number;
    protocol: "udp" | "tcp";
    description: string;
}

/**
 * GET /api/v1/serverlog/:subscriptionId/?page=1&limit=99999&tenantId=:tenantId
 * Gets list of syslog servers for V1.
 * @param subscriptionId - Subscription ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 99999)
 */
export function getSyslogServersV1(
    subscriptionId: number,
    tenantId: number,
    token: string,
    page = 1,
    limit = 99999
) {
    const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_V1_URL}/serverlog/${subscriptionId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * GET /api/v1/serverlog/:subscriptionId/:serverId?tenantId=:tenantId
 * Gets a single syslog server by ID for V1.
 * @param subscriptionId - Subscription ID
 * @param serverId - Syslog server ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 */
export function getSyslogServerV1(
    subscriptionId: number,
    serverId: string | number,
    tenantId: number,
    token: string
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.get(`${API_V1_URL}/serverlog/${subscriptionId}/${serverId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * POST /api/v1/serverlog/:subscriptionId/?tenantId=:tenantId
 * Creates a new syslog server for V1.
 * @param subscriptionId - Subscription ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param data - Syslog server payload
 */
export function createSyslogServerV1(
    subscriptionId: number,
    tenantId: number,
    token: string,
    data: SyslogServerRequestV1
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.post(`${API_V1_URL}/serverlog/${subscriptionId}?${params.toString()}`, data, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * PUT /api/v1/serverlog/:subscriptionId/:serverId?tenantId=:tenantId
 * Updates an existing syslog server for V1.
 * @param subscriptionId - Subscription ID
 * @param serverId - Syslog server ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param data - Syslog server payload
 */
export function updateSyslogServerV1(
    subscriptionId: number,
    serverId: string | number,
    tenantId: number,
    token: string,
    data: SyslogServerRequestV1
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.put(`${API_V1_URL}/serverlog/${subscriptionId}/${serverId}?${params.toString()}`, data, {
        headers: { Authorization: `Bearer ${token}` },
    });
}

/**
 * DELETE /api/v1/serverlog/:subscriptionId/:serverId?tenantId=:tenantId
 * Deletes a syslog server for V1.
 * @param subscriptionId - Subscription ID
 * @param serverId - Syslog server ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 */
export function deleteSyslogServerV1(
    subscriptionId: number,
    serverId: string | number,
    tenantId: number,
    token: string
) {
    const params = new URLSearchParams({
        tenantId: tenantId.toString()
    });
    return axios.delete(`${API_V1_URL}/serverlog/${subscriptionId}/${serverId}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
}
